import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@mui/material';
import { getLabel, getMessage } from '@shared/utils';
import { getFormattedDate } from '@shared/utils/dateUtils';

import type { InterruptedCashinNotice as InterruptedCashinNoticeState } from './useInterruptedCashin';

interface InterruptedCashinNoticeProps {
  notice: InterruptedCashinNoticeState;
  onClose: () => void;
}

const titleFor = (state: InterruptedCashinNoticeState['state']): string => {
  if (state === 'completed') {
    return getMessage('cashinInterruptedCompletedTitle');
  }
  if (state === 'rejected') {
    return getMessage('cashinVerifiedRejectedTitle');
  }
  if (state === 'expired') {
    return getMessage('cashinUnverifiedTitle');
  }
  return getMessage('cashinIndeterminateTitle');
};

const receiptDate = (value: unknown): string => {
  if (typeof value !== 'string') {
    return '';
  }
  try {
    return getFormattedDate(value);
  } catch {
    return '';
  }
};

const detailRows = (notice: InterruptedCashinNoticeState) => {
  const { pending } = notice;
  const transfer = notice.transfer?.[0];
  const rows: { label: string; value: string; small?: boolean }[] = [
    { label: getMessage('cashinInterruptedDepositor'), value: pending.ci_sender },
    { label: getLabel('cellphone'), value: pending.cellphone },
    { label: getLabel('amount'), value: pending.amount ? `Bs ${pending.amount}` : '' },
  ];
  if (transfer) {
    rows.push(
      { label: getLabel('transactionId'), value: String(transfer.domPayId ?? '') },
      { label: getLabel('dateAndTime'), value: receiptDate(transfer.created_at) }
    );
  } else {
    rows.push({ label: getMessage('cashinOperationCode'), value: pending.request_id, small: true });
  }
  return rows;
};

const NoticeBody = ({ notice }: { notice: InterruptedCashinNoticeState }) => {
  if (notice.state === 'completed') {
    return null;
  }
  if (notice.state === 'rejected') {
    return (
      <Typography sx={{ color: '#646166', fontSize: '14px', textAlign: 'center' }}>
        {getMessage('cashinVerifiedRejectedBody')}
        {notice.error?.message ? ` ${notice.error.message}` : ''}
      </Typography>
    );
  }
  if (notice.state === 'expired') {
    return (
      <Typography sx={{ color: '#646166', fontSize: '14px', textAlign: 'center' }}>
        {getMessage('cashinUnverifiedBody')}
      </Typography>
    );
  }
  return (
    <Box>
      <Typography sx={{ color: '#646166', fontSize: '14px', textAlign: 'center' }}>
        {getMessage('cashinIndeterminate')}
      </Typography>
      <LinearProgress color="warning" sx={{ marginTop: '12px' }} />
    </Box>
  );
};

const PendingCashinDetails = ({ notice }: { notice: InterruptedCashinNoticeState }) => (
  <Box
    sx={{
      marginTop: '16px',
      borderRadius: '16px',
      backgroundColor: '#F8F9FC',
      border: '1px solid #F0EEF2',
      overflow: 'hidden',
    }}>
    {detailRows(notice).map((row, index) => (
      <Box
        key={row.label}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 16px',
          borderTop: index === 0 ? 'none' : '1px solid #F0EEF2',
        }}>
        <Typography sx={{ color: '#646166', fontSize: '13px', lineHeight: 1.3 }}>{row.label}</Typography>
        <Typography
          sx={{
            color: '#332D3A',
            fontSize: row.small ? '12px' : '15px',
            fontWeight: row.small ? 400 : 600,
            lineHeight: 1.3,
            textAlign: 'right',
            wordBreak: 'break-all',
          }}>
          {row.value || '—'}
        </Typography>
      </Box>
    ))}
  </Box>
);

const dialogActionsSx = {
  flexDirection: 'column',
  alignItems: 'stretch',
  paddingInline: '32px',
  paddingBottom: '32px',
  paddingTop: '16px',
  gap: '12px',
  '& > :not(:first-of-type)': { marginLeft: 0 },
} as const;

export const InterruptedCashinNotice = ({ notice, onClose }: InterruptedCashinNoticeProps) => {
  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open
      onClose={onClose}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
        },
        '& .MuiBackdrop-root': {
          backdropFilter: 'blur(5px)',
        },
        '& .MuiDialog-paper': {
          borderRadius: '24px',
          marginTop: '72px',
        },
      }}>
      <DialogTitle
        sx={{
          textAlign: 'center',
          color: '#332D3A',
          fontWeight: 700,
          paddingInline: '32px',
          paddingTop: '32px',
          paddingBottom: '8px',
        }}>
        {titleFor(notice.state)}
      </DialogTitle>
      <DialogContent sx={{ paddingInline: '32px', paddingBottom: '8px' }}>
        <NoticeBody notice={notice} />
        <PendingCashinDetails notice={notice} />
      </DialogContent>
      <DialogActions sx={dialogActionsSx}>
        <Button
          variant="contained"
          disableElevation
          onClick={onClose}
          sx={{
            height: '48px',
            borderRadius: '8px',
            backgroundColor: '#7025c6',
            color: '#ffffff',
            fontSize: '16px',
            fontWeight: 700,
            textTransform: 'none',
          }}>
          <span style={{ color: '#ffffff' }}>{getMessage('ok')}</span>
        </Button>
      </DialogActions>
    </Dialog>
  );
};
